import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {PageEvent} from '@angular/material/paginator';
import {NgxSpinnerService} from 'ngx-spinner';

import {BehaviorSubject, combineLatest, firstValueFrom, throwError} from 'rxjs';
import {catchError, delay, filter, first, map, switchMap, tap} from 'rxjs/operators';
import {fromEvent, Subscription} from 'rxjs';

import {filePath} from '@atmi/core';
import {makeQueryBuilder, makeQueryRules, TableFilterType} from '@ui/ui.module';

import {Media} from '@sim/media';
import {MediaFiltersComponent} from '@sim/media/components/media-filters/media-filters.component';
import {MediaExt, MediaFilter, MediaFilterMeta} from '@sim/media/models/media';
import {MediaService} from '@sim/media/services/media.service';

export interface ImagePickerDialogData {
  selection?: 'single' | 'multiple';
  /** Media type to exclude */
  excludedExtensions?: MediaExt[];
}

const PAGINATOR = {length: 48, pageIndex: 0, pageSize: 48};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-image-picker-dialog',
  styleUrls: ['./image-picker-dialog.component.scss'],
  templateUrl: './image-picker-dialog.component.html',
})
export class ImagePickerDialogComponent implements OnInit, OnDestroy {

  isLoading = false;
  isUploading = false;
  selectedMedia: Media[] = [];

  // Range selection properties
  lastClickedIndex: number | null = null;
  lastClickedMediaId: string | null = null;
  currentPageMedia: any[] = [];
  currentRangeStart: number | null = null;
  currentRangeEnd: number | null = null;

  readonly MobileFiltersComponent = MediaFiltersComponent;

  private readonly paginator$ = new BehaviorSubject<PageEvent | undefined>(PAGINATOR);
  private readonly filters$ = new BehaviorSubject<TableFilterType | undefined>(undefined);
  private readonly refreshList$ = new BehaviorSubject<undefined>(undefined);

  readonly media$ = combineLatest([
    this.paginator$.pipe(filter((paginator): paginator is PageEvent => !!paginator)),
    this.filters$,
    this.refreshList$,
  ]).pipe(
    switchMap(([paginator, filters]) => this.mediaApi.index({
      filter: filters,
      limit: paginator.pageSize,
      page: paginator.pageIndex + 1,
      sort: [{field: 'createdAt', type: 'desc'}],
    })),
    map((mediaPage) => {
      const data = mediaPage.data.map((m) => ({
        ...m,
        previewUrl: `${this.fileUrl}${m.previewUrl}`,
        name: m.name || '',
        type: m.type as string || '',
      }));

      // Update current page media for range selection
      this.currentPageMedia = data as any;

      // Calculate total pages and update page numbers
      this.totalPages = Math.ceil(mediaPage.count / mediaPage.params.limit);
      this.pageNumbers = Array.from({length: this.totalPages}, (_, i) => i + 1);

      return {...mediaPage, data};
    }),
    delay(500),
    tap(() => this.stopLoadingUi()),
    catchError((e) => {
      this.stopLoadingUi();

      return throwError(e);
    }),
  );

  private keyboardSubscription: Subscription = new Subscription();

  readonly pageSizeOptions = [8, 12, 24, 48];
  
  currentPageIndex = 0;
  totalPages = 0;
  pageNumbers: number[] = [];

  constructor(
    private readonly mediaApi: MediaService,
    private readonly dialogRef: MatDialogRef<ImagePickerDialogComponent>,
    private readonly spinnerService: NgxSpinnerService,
    private readonly snackBar: MatSnackBar,
    @Inject(filePath) private readonly fileUrl: string,
    @Inject(MAT_DIALOG_DATA) public readonly data: ImagePickerDialogData,
  ) {
    this.data = {
      selection: 'multiple',
      ...this.data,
    };

    const notExt = this.data.excludedExtensions || [];

    this.applyFilterWithDelayHandler({notExt: notExt[0]});
    this.loadingUi();
  }

  ngOnInit() {
    // No longer need keyboard event listeners as we'll rely on event.shiftKey
  }

  ngOnDestroy() {
    if (this.keyboardSubscription) {
      this.keyboardSubscription.unsubscribe();
    }
  }

  onMediaClick(event: MouseEvent, media: Media & { name: string, type: string }) {
    // Find the index of the clicked media in the current page
    const currentIndex = this.currentPageMedia.findIndex(m => m.id === media.id);

    if (currentIndex === -1) {
      return; // Media not found in current page
    }

    // Handle Shift+Click for range selection
    if (event.shiftKey && this.lastClickedIndex !== null && this.data.selection === 'multiple') {
      // Check if Shift-clicking the same item - do nothing in this case
      if (this.lastClickedIndex === currentIndex) {
        return;
      }

      this.handleRangeSelection(currentIndex);
      return;
    }

    // Regular click handling
    this.toggleMediaHandler(media);

    // Update the last clicked index and media ID for potential future range selection
    this.lastClickedIndex = currentIndex;
    this.lastClickedMediaId = media.id || null;

    // Reset current range when starting a new selection
    this.currentRangeStart = null;
    this.currentRangeEnd = null;
  }

  toggleMediaHandler(media: Media & { name: string, type: string }) {
    if (this.data.selection && this.data.selection === 'single') {
      this.selectedMedia = [media];

      return;
    }

    if (this.selectedMedia.find((m) => m.id === media.id)) {
      this.selectedMedia = this.selectedMedia.filter((m) => m.id !== media.id);
    } else {
      this.selectedMedia = [...this.selectedMedia, media];
    }
  }

  private handleRangeSelection(currentIndex: number) {
    if (this.lastClickedIndex === null) {
      return;
    }

    // Determine the range between the last clicked item and the current item
    const startIndex = Math.min(this.lastClickedIndex, currentIndex);
    const endIndex = Math.max(this.lastClickedIndex, currentIndex);

    // Store the current range for visual feedback
    this.currentRangeStart = startIndex;
    this.currentRangeEnd = endIndex;

    // Get all media items in the range
    const rangeItems = this.currentPageMedia.slice(startIndex, endIndex + 1);

    // Add the range items to the selection
    this.addRangeToSelection(rangeItems);
  }

  private addRangeToSelection(rangeItems: (Media & { name: string, type: string })[]) {
    // Create a Set of currently selected media IDs for efficient lookup
    const selectedIds = new Set(this.selectedMedia.map(m => m.id));

    // Add all items in the range to the selection
    const newSelection = [...this.selectedMedia];

    rangeItems.forEach(item => {
      if (!selectedIds.has(item.id)) {
        newSelection.push(item);
        selectedIds.add(item.id);
      }
    });

    this.selectedMedia = newSelection;
  }

  isMediaSelected(media: Media & { name: string, type: string }) {
    return this.selectedMedia.find((m) => m.id === media.id);
  }

  isMediaInRangeSelection(media: Media & { name: string, type: string }) {
    // Check if the media is in the current range selection
    if (this.currentRangeStart === null || this.currentRangeEnd === null) {
      return false;
    }

    const mediaIndex = this.currentPageMedia.findIndex(m => m.id === media.id);
    if (mediaIndex === -1) {
      return false;
    }

    const startIndex = Math.min(this.currentRangeStart, this.currentRangeEnd);
    const endIndex = Math.max(this.currentRangeStart, this.currentRangeEnd);

    return mediaIndex >= startIndex && mediaIndex <= endIndex;
  }

  changePageHandler(pageEvent: PageEvent) {
    // Reset range selection state when page changes
    this.resetRangeSelectionState();
    this.currentPageIndex = pageEvent.pageIndex;
    this.loadingUi();
    this.paginator$.next(pageEvent);
  }

  jumpToPage(pageIndex: number) {
    const currentPaginator = this.paginator$.value;
    if (!currentPaginator) {
      return;
    }

    const pageEvent: PageEvent = {
      pageIndex,
      pageSize: currentPaginator.pageSize,
      length: currentPaginator.length,
    };

    this.changePageHandler(pageEvent);
  }

  closeHandler() {
    // Reset range selection state when dialog closes
    this.resetRangeSelectionState();
    this.dialogRef.close();
  }

  saveHandler() {
    this.dialogRef.close(this.selectedMedia);
  }

  applyFilterWithDelayHandler(filters: MediaFilter) {
    // Reset range selection state when filters change
    this.resetRangeSelectionState();
    const rules = makeQueryRules(filters, MediaFilterMeta);
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }

  private resetRangeSelectionState() {
    this.lastClickedIndex = null;
    this.lastClickedMediaId = null;
    this.currentRangeStart = null;
    this.currentRangeEnd = null;
  }

  async uploadHandler(fileList: File[]) {
    if (!fileList.length) {
      return;
    }

    this.isUploading = true;

    try {
      await firstValueFrom(this.mediaApi.post(fileList).pipe(first()));

      this.refreshList$.next(undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload files.';
      this.snackBar.open(message, undefined, {duration: 5000});
    } finally {
      this.isUploading = false;
    }
  }

  private loadingUi() {
    this.spinnerService.show('loadingMedia');
    this.isLoading = true;
  }

  private stopLoadingUi() {
    this.spinnerService.hide('loadingMedia');
    this.isLoading = false;
  }
}
